import React, { useEffect } from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import SEO from './SEO';

interface Props {
  onNavigate: (view: any, sectionId?: string) => void;
}

const WhiteLabelSupplements: React.FC<Props> = ({ onNavigate }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-24 pb-16 bg-white">
      <SEO 
        title="White Label Supplements No Minimum | BuildMySupplement" 
        description="Speed and simplicity. Choose from 100+ ready formulas, add your custom label, and start selling your white label supplements fast. No minimum order quantities for small brands." 
      />
      
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-orange-100/80 border border-orange-200 mb-6">
            <span className="text-orange-800 text-xs font-bold uppercase tracking-widest">
              Speed & Simplicity
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 tracking-tight mb-8 leading-tight">
            Launch Your Brand Today with Premium <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500">White Label Supplements</span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-3xl mx-auto">
            You don't need millions in capital or a degree in food science to build a premium DTC wellness brand. Choose from over 100 heavily tested, proven formulations, slap on your custom label, and start selling immediately with zero minimum order quantities.
          </p>
          <button 
            onClick={() => onNavigate('contact')}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-black rounded-full hover:bg-gray-800 transition-all duration-200 transform hover:-translate-y-1"
          >
            Start Your Brand Now <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Content Sections */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-lg text-gray-700 space-y-12">
        
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">The Fast Track to Market: White Label Supplements No Minimum</h2>
          <p className="mb-4 leading-relaxed">
            In the modern consumer landscape, speed is your greatest asset. Consumer trends pivot rapidly—today it’s adaptogens, tomorrow it’s nootropics. If you spend eighteen months in research and development, you run the risk of missing the wave entirely. This is why <strong>white label supplements</strong> have become the dominant strategy for agile, fast-growing brands.
          </p>
          <p className="mb-4 leading-relaxed">
            By leveraging our catalog of ready-to-label formulas, you bypass the friction. There are no formulation dead-ends, no stability issues to troubleshoot, and no massive factory deposits required. We believe that entry barriers shouldn't gatekeep innovation, which is why we’ve revolutionized the industry by offering <strong>white label supplements no minimum</strong>. Whether you want to test the waters with 50 units or scale up to 5,000, we adapt to your growth.
          </p>
        </div>

        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">Built for Scale: Your White Label Supplement Manufacturer USA</h2>
          <p className="mb-4 leading-relaxed">
            Not all manufacturers are created equal. When you choose an overseas facility to cut pennies on the dollar, you often sacrifice the quality and transparency that modern consumers demand. As a premier <strong>white label supplement manufacturer USA</strong>, we operate strictly within GMP-certified (Good Manufacturing Practice) facilities. 
          </p>
          <p className="mb-4 leading-relaxed">
            What does this mean for your brand? It means every capsule, powder, and gummy you sell carries the integrity of rigorous third-party testing. It means your ingredient transparency is flawless. When your customers look at your label, they see a premium product they can trust. We worry about compliance, raw material sourcing, and quality control so you can focus exclusively on community building and sales.
          </p>
          <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 mt-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Why our manufacturing standards matter:</h3>
            <ul className="space-y-4">
              <li className="flex items-start"><CheckCircle2 className="w-6 h-6 text-orange-500 mr-3 flex-shrink-0" /> <span><strong>FDA Registered Facilities:</strong> Adhering strictly to federal guidelines for safety and purity.</span></li>
              <li className="flex items-start"><CheckCircle2 className="w-6 h-6 text-orange-500 mr-3 flex-shrink-0" /> <span><strong>Third-Party Tested:</strong> Every batch is verified for potency and free from heavy metals or contaminants.</span></li>
              <li className="flex items-start"><CheckCircle2 className="w-6 h-6 text-orange-500 mr-3 flex-shrink-0" /> <span><strong>Premium Raw Materials:</strong> We source clinically backed ingredients for maximum efficacy.</span></li>
            </ul>
          </div>
        </div>

        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">Over 100 Proven Formulations Ready for Your Brand</h2>
          <p className="mb-4 leading-relaxed">
            Variety creates opportunity. Our extensive catalog isn't just a list of basic vitamins; it’s a meticulously curated collection of high-demand supplements designed to perform well in today's highly competitive e-commerce ecosystem. 
          </p>
          <p className="mb-4 leading-relaxed">
            Are you targeting the high-performance fitness crowd? Drop your logo onto our scientifically dosed pre-workouts and post-workout amino acids. Are you catering to stressed professionals? Explore our adaptogenic ashwagandha blends and sleep support formulas. We offer <strong>white label supplements for small brands</strong> that allow you to build an entire product ecosystem from day one, turning single-product buyers into recurring subscribers with high lifetime value.
          </p>
        </div>

        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">From Factory Floor to Your Customer’s Door in Record Time</h2>
          <p className="mb-4 leading-relaxed">
            Supply chain bottlenecks can suffocate a growing DTC brand. You run a viral marketing campaign, sell out of your product, and then lose momentum because your manufacturer takes three months to restock. We solve that.
          </p>
          <p className="mb-4 leading-relaxed">
            Because our white label catalog features in-house, pre-manufactured bulk inventory, our turnaround times are incredibly compressed. When you place an order, our job is simply to apply your beautifully designed labels and ship it out. In many cases, we can turn orders around in weeks, not months. This unparalleled speed allows you to stay liquid, pivot instantly, and capitalize on trends while they are still hot.
          </p>
        </div>

        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">The Process: As Simple As 1-2-3</h2>
          <p className="mb-4 leading-relaxed">
            We operate like a true partner, handling the heavy lifting of the supply chain. Here is exactly how you go from an idea to holding your premium supplement brand in your hands:
          </p>
          <ul className="space-y-6 mt-6">
            <li className="flex">
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 text-orange-700 font-bold text-xl border border-orange-200">1</div>
              <div className="ml-6">
                <h4 className="text-xl font-bold text-gray-900">Select Your Formula</h4>
                <p className="text-gray-600 mt-2">Browse our high-converting catalog of over 100 scientifically backed formulations.</p>
              </div>
            </li>
            <li className="flex">
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 text-orange-700 font-bold text-xl border border-orange-200">2</div>
              <div className="ml-6">
                <h4 className="text-xl font-bold text-gray-900">Design Your Label</h4>
                <p className="text-gray-600 mt-2">Work with our in-house design team to create a visually stunning, FSSAI/FDA compliant label that matches your brand’s aesthetic perfectly.</p>
              </div>
            </li>
            <li className="flex">
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 text-orange-700 font-bold text-xl border border-orange-200">3</div>
              <div className="ml-6">
                <h4 className="text-xl font-bold text-gray-900">Launch and Scale</h4>
                <p className="text-gray-600 mt-2">Receive your market-ready products with ultra-fast turnaround times. Start marketing and watch the revenue compound.</p>
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-orange-50 border-t border-b border-orange-100 py-20 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-6">Stop Waiting. Start Building.</h2>
          <p className="text-xl text-gray-700 mb-10 max-w-2xl mx-auto">
            Take hold of your creator monetization. Launch your premium white label supplement brand today with zero minimums and maximum quality.
          </p>
          <button 
            onClick={() => onNavigate('contact')}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-orange-600 rounded-full hover:bg-orange-700 transition-all duration-200 transform hover:-translate-y-1 shadow-lg hover:shadow-orange-500/25"
          >
            Get Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default WhiteLabelSupplements;
